import dayjs from 'dayjs';

import {
    ReportParams,
    StatusListParams,
    TestModeStatusRow,
    TestModeStatusPage,
    WhoTestedRow,
} from './testMode.types';

/**
 * DEV-ONLY sample data for the Test Mode tab.
 *
 * The status-list / report endpoints read the SQL warehouse, which is
 * VPC-only (unreachable from a local machine) and empty until the
 * etl-service deploys `TestModeSessions`. These fixtures let the UI be
 * exercised visually in the meantime — every row state in the design is
 * represented, including fields the backend does not return yet.
 *
 * Enable in the browser console:  localStorage.setItem('testModeFixtures', '1')
 * Disable:                        localStorage.removeItem('testModeFixtures')
 */
export const testModeFixturesEnabled = (): boolean =>
    process.env.NODE_ENV === 'development' &&
    typeof window !== 'undefined' &&
    window.localStorage.getItem('testModeFixtures') === '1';

const h = (hours: number) => dayjs().add(hours, 'hour').toISOString();

type FixtureSeed = Partial<TestModeStatusRow> &
    Pick<TestModeStatusRow, 'userId' | 'name' | 'team' | 'group' | 'inTest' | 'testedApp' | 'testedDevice' | 'lastEnteredAt'>;

const row = (seed: FixtureSeed): TestModeStatusRow => ({
    devicePaired: 0,
    exitedWithoutTest: 0,
    pairedDeviceUntested: 0,
    expiresAt: null,
    ...seed,
});

export const FIXTURE_ROWS: TestModeStatusRow[] = [
    row({
        userId: 'fx-01', name: 'Maria Alvarez', team: 'North Field Team', group: 'Northeast Region',
        employeeId: 'EMP-10421', inTest: 1, testedApp: 0, testedDevice: 0,
        lastEnteredAt: h(-2), expiresAt: h(22),
    }),
    row({
        userId: 'fx-02', name: 'James Chen', team: 'North Field Team', group: 'Northeast Region',
        employeeId: 'EMP-10422', inTest: 1, testedApp: 1, testedDevice: 0,
        lastEnteredAt: h(-5), expiresAt: h(19), devicePaired: 1, pairedDeviceUntested: 1,
    }),
    row({
        userId: 'fx-03', name: 'Aisha Roberts', team: 'City Clinical', group: 'Northeast Region',
        employeeId: 'EMP-10387', inTest: 1, testedApp: 0, testedDevice: 0,
        lastEnteredAt: h(-23), expiresAt: h(1),
    }),
    row({
        userId: 'fx-04', name: 'Devon Parker', team: 'City Clinical', group: 'Northeast Region',
        employeeId: 'EMP-10318', inTest: 0, testedApp: 1, testedDevice: 1,
        lastEnteredAt: h(-50), devicePaired: 1,
    }),
    row({
        userId: 'fx-05', name: 'Priya Natarajan', team: 'Home Visits West', group: 'Western Region',
        employeeId: 'EMP-09984', inTest: 0, testedApp: 0, testedDevice: 0,
        lastEnteredAt: h(-70), exitedWithoutTest: 1,
    }),
    row({
        userId: 'fx-06', name: 'Tom Okafor', team: 'Home Visits West', group: 'Western Region',
        employeeId: 'EMP-09990', inTest: 1, testedApp: 1, testedDevice: 1,
        lastEnteredAt: h(-1), expiresAt: h(23), devicePaired: 1,
    }),
    row({
        userId: 'fx-07', name: 'Hannah Weiss', team: 'Overnight Response', group: 'Western Region',
        employeeId: 'EMP-10005', inTest: 1, testedApp: 0, testedDevice: 0,
        lastEnteredAt: h(-3), expiresAt: h(21), exitRequested: true,
    }),
    row({
        userId: 'fx-08', name: 'Luis Fernandez', team: 'Overnight Response', group: 'Western Region',
        employeeId: 'EMP-10007', inTest: 0, testedApp: 1, testedDevice: 0,
        lastEnteredAt: h(-30), devicePaired: 1, pairedDeviceUntested: 1,
    }),
];

/** Report rows derived from the same sample users (registered = all). */
export const fixtureReportRows = (_params: ReportParams): WhoTestedRow[] =>
    FIXTURE_ROWS.map((r, i) => ({
        userId: r.userId,
        name: r.name,
        team: r.team,
        group: r.group,
        registered: 1,
        paired: r.devicePaired ? 1 : 0,
        testedApp: r.testedApp,
        testedDevice: r.testedDevice,
        sessionCount: (i % 3) + 1,
        lastEnteredAt: r.lastEnteredAt,
    }));

/** Apply the server's filter/pagination semantics to the fixture set. */
export const fixtureStatusPage = (params: StatusListParams): TestModeStatusPage => {
    let rows = FIXTURE_ROWS;
    if (params.status === 'in-test') rows = rows.filter((r) => r.inTest === 1);
    if (params.status === 'not-in-test') rows = rows.filter((r) => r.inTest === 0);
    if (params.status === 'flagged') rows = rows.filter((r) => r.exitedWithoutTest || r.pairedDeviceUntested);
    if (params.testCompleted === true) rows = rows.filter((r) => r.testedApp === 1);
    if (params.testCompleted === false) rows = rows.filter((r) => r.testedApp === 0);
    if (params.device === 'none') rows = rows.filter((r) => !r.devicePaired);
    if (params.device === 'paired-untested') rows = rows.filter((r) => Boolean(r.devicePaired) && !r.testedDevice);
    if (params.device === 'paired-tested') rows = rows.filter((r) => r.testedDevice === 1);
    if (params.name) {
        const q = params.name.toLowerCase();
        rows = rows.filter((r) => r.name.toLowerCase().includes(q));
    }
    const start = (params.page - 1) * params.perPage;
    return {
        items: rows.slice(start, start + params.perPage),
        page: params.page,
        perPage: params.perPage,
        total: rows.length,
    };
};
